import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { BookingService } from '../../services/booking.service';
import * as BookingActions from './bookings.actions';

@Injectable()
export class BookingsEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _bookingService = inject(BookingService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all bookings',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Bookings
    getAllBookings$ = createEffect(() =>
        this._actions$.pipe(
            ofType(BookingActions.loadBookings),
            switchMap((action) => {
                return this._bookingService.getAll(action.getParams).pipe(
                    map((res) => {
                        return BookingActions.loadBookingsSuccess({
                            bookings: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            BookingActions.loadBookingsFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Booking
    getBooking$ = createEffect(() =>
        this._actions$.pipe(
            ofType(BookingActions.loadBooking),
            switchMap((action) => {
                return this._bookingService.getOne(action.id).pipe(
                    map((res) => {
                        return BookingActions.loadBookingSuccess({
                            booking: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            BookingActions.loadBookingFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create Booking
    createBooking$ = createEffect(() =>
        this._actions$.pipe(
            ofType(BookingActions.createBooking),
            switchMap((action) => {
                return this._bookingService.create(action.data).pipe(
                    map((res) => {
                        return BookingActions.createBookingSuccess({
                            booking: res.data,
                            success: 'Booking created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            BookingActions.createBookingFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update Booking
    updateBooking$ = createEffect(() =>
        this._actions$.pipe(
            ofType(BookingActions.updateBooking),
            switchMap((action) => {
                return this._bookingService.update(action.data, action.id).pipe(
                    map((res) => {
                        return BookingActions.updateBookingSuccess({
                            booking: res.data,
                            success: 'Booking updated successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            BookingActions.updateBookingFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Delete Booking
    deleteBooking$ = createEffect(() =>
        this._actions$.pipe(
            ofType(BookingActions.deleteBooking),
            switchMap((action) => {
                return this._bookingService.delete(action.id).pipe(
                    map(() => {
                        return BookingActions.deleteBookingSuccess({
                            id: action.id,
                            success: 'Booking deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            BookingActions.deleteBookingFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onBookingActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    BookingActions.createBookingSuccess,
                    BookingActions.updateBookingSuccess,
                    BookingActions.deleteBookingSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onBookingActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    BookingActions.loadBookingsFailure,
                    BookingActions.loadBookingFailure,
                    BookingActions.createBookingFailure,
                    BookingActions.updateBookingFailure,
                    BookingActions.deleteBookingFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
